import { useState } from 'react';
import { Play, Loader2, ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import ConfigField from '../ConfigField';

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

// Recursively flatten an object into dot-notation paths.
// Arrays peek into the first element using [] notation.
function flattenKeys(obj, prefix = '', depth = 0) {
  if (depth > 4 || obj == null) return [];
  const keys = [];
  if (Array.isArray(obj)) {
    keys.push({ key: prefix || 'data', label: prefix || 'Data (Array)' });
    if (obj.length > 0 && typeof obj[0] === 'object' && obj[0] !== null) {
      keys.push(...flattenKeys(obj[0], `${prefix}[]`, depth + 1));
    }
    return keys;
  }
  if (typeof obj === 'object') {
    for (const [k, v] of Object.entries(obj)) {
      const path = prefix ? `${prefix}.${k}` : k;
      if (v != null && typeof v === 'object') {
        keys.push(...flattenKeys(v, path, depth + 1));
      } else {
        keys.push({ key: path, label: path });
      }
    }
  }
  return keys;
}

export default function ApiRequestConfig({ data, onChange }) {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [resultExpanded, setResultExpanded] = useState(true);

  const headers = data.headers || [];

  const addHeader = () => {
    onChange('headers', [...headers, { key: '', value: '' }]);
  };

  const updateHeader = (index, field, value) => {
    const updated = headers.map((h, i) => (i === index ? { ...h, [field]: value } : h));
    onChange('headers', updated);
  };

  const removeHeader = (index) => {
    onChange('headers', headers.filter((_, i) => i !== index));
  };

  const handleTest = async () => {
    if (!data.url) return;
    setTesting(true);
    setTestResult(null);

    try {
      const reqHeaders = {};
      for (const h of headers) {
        if (h.key) reqHeaders[h.key] = h.value;
      }

      const opts = {
        method: data.method || 'GET',
        headers: { 'Content-Type': 'application/json', ...reqHeaders },
      };
      if (data.body && ['POST', 'PUT', 'PATCH'].includes(opts.method)) {
        opts.body = data.body;
      }

      const res = await fetch(data.url, opts);
      const contentType = res.headers.get('content-type') || '';
      let body;
      if (contentType.includes('application/json')) {
        body = await res.json();
      } else {
        body = await res.text();
      }

      const result = { ok: res.ok, status: res.status, statusText: res.statusText, body };
      setTestResult(result);

      // Persist flattened response keys so VariablePicker can offer them
      if (res.ok && body != null && typeof body === 'object') {
        const params = flattenKeys(body, 'data');
        onChange('lastTestParameters', params);
      }
    } catch (err) {
      setTestResult({ ok: false, status: 0, statusText: 'Network Error', body: err.message });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Name */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1.5">Response Variable Name</label>
        <input
          type="text"
          value={data.responseName || ''}
          onChange={(e) => onChange('responseName', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
          placeholder="e.g. apiResult"
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 font-mono text-xs"
        />
        <p className="text-[10px] text-gray-400 mt-1">
          Response available as <code className="text-blue-600 bg-blue-50 px-1 rounded">{`{{${data.responseName || 'name'}.data}}`}</code>
        </p>
      </div>

      {/* Method */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1.5">Method</label>
        <select
          value={data.method || 'GET'}
          onChange={(e) => onChange('method', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
        >
          {METHODS.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>

      {/* URL */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1.5">URL</label>
        <ConfigField
          field={{ key: 'url', type: 'text', placeholder: 'https://api.example.com/data' }}
          value={data.url}
          onChange={(v) => onChange('url', v)}
          allData={data}
          onChangeField={onChange}
        />
      </div>

      {/* Headers */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs font-medium text-gray-600">Headers</label>
          <button
            type="button"
            onClick={addHeader}
            className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition"
          >
            <Plus className="w-3 h-3" />
            Add
          </button>
        </div>
        {headers.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-2">No custom headers.</p>
        )}
        {headers.map((header, index) => (
          <div key={index} className="bg-gray-50 rounded-lg p-2 mb-1.5 space-y-1.5">
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                value={header.key}
                onChange={(e) => updateHeader(index, 'key', e.target.value)}
                placeholder="Key"
                className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 font-mono"
              />
              <button
                type="button"
                onClick={() => removeHeader(index)}
                className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition shrink-0"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <input
              type="text"
              value={header.value}
              onChange={(e) => updateHeader(index, 'value', e.target.value)}
              placeholder="Value"
              className="w-full min-w-0 px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 font-mono"
            />
          </div>
        ))}
      </div>

      {/* Body (only for methods that support it) */}
      {['POST', 'PUT', 'PATCH'].includes(data.method) && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1.5">Body</label>
          <ConfigField
            field={{ key: 'body', type: 'textarea', placeholder: '{"key": "value"}' }}
            value={data.body}
            onChange={(v) => onChange('body', v)}
            allData={data}
            onChangeField={onChange}
          />
        </div>
      )}

      {/* Test button */}
      <div className="h-px bg-gray-100 my-1" />
      <button
        type="button"
        onClick={handleTest}
        disabled={testing || !data.url}
        className="flex items-center justify-center gap-2 w-full px-3 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition"
      >
        {testing ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Play className="w-4 h-4" />
        )}
        {testing ? 'Testing...' : 'Test Request'}
      </button>

      {/* Test result */}
      {testResult && (
        <div className={`rounded-lg border ${testResult.ok ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
          <button
            type="button"
            onClick={() => setResultExpanded(!resultExpanded)}
            className="flex items-center gap-2 w-full px-3 py-2 text-left"
          >
            {resultExpanded ? (
              <ChevronDown className="w-3 h-3 text-gray-400 shrink-0" />
            ) : (
              <ChevronRight className="w-3 h-3 text-gray-400 shrink-0" />
            )}
            <span className={`text-xs font-semibold ${testResult.ok ? 'text-emerald-700' : 'text-red-700'}`}>
              {testResult.status} {testResult.statusText}
            </span>
          </button>
          {resultExpanded && (
            <div className="px-3 pb-3">
              <pre className="text-[10px] text-gray-700 bg-white rounded-md p-2 border border-gray-200 overflow-x-auto max-h-48 overflow-y-auto font-mono whitespace-pre-wrap break-all">
                {typeof testResult.body === 'object'
                  ? JSON.stringify(testResult.body, null, 2)
                  : testResult.body}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
